import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Table } from "ant-table-extensions";
import { Badge, Button, Input, PageHeader, Row, Select, Tag, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import api from "../api";
import CustomPaginationNav from "../components/CustomPaginationNav";

const { Option } = Select;

// Types and Constants
type CouponStatus = "active" | "pending" | "expired";

const STATUS_COLORS: Record<CouponStatus, string> = {
  active: "green",
  pending: "orange",
  expired: "grey",
};

const STATUS_LABELS: Record<CouponStatus, string> = {
  active: "Gültig",
  pending: "Ausstehend",
  expired: "Abgelaufen",
};

// Helper Functions
const getCouponStatus = (coupon: Coupon): CouponStatus => {
  const now = new Date();
  const useStart = coupon.useStart ? new Date(coupon.useStart) : null;
  const useEnd = coupon.useEnd ? new Date(coupon.useEnd) : null;

  if (useEnd && now > useEnd) {
    return "expired";
  }

  if (!coupon.unlocked) {
    return "pending";
  }

  if (useStart && now < useStart) {
    return "pending";
  }

  return "active";
};

const sortCouponsData = (data: Coupon[]): Coupon[] => {
  return [...data].sort((a, b) => {
    const statusA = getCouponStatus(a);
    const statusB = getCouponStatus(b);

    // First, group by status (active first, then pending, then expired)
    if (statusA === "active" && statusB !== "active") return -1;
    if (statusA !== "active" && statusB === "active") return 1;
    if (statusA === "pending" && statusB === "expired") return -1;
    if (statusA === "expired" && statusB === "pending") return 1;

    // Within the same status group, sort by CID descending (newest first)
    return b.cid - a.cid;
  });
};

const getDirection = (type: any): string => {
  return type === "" ? "asc" : type === "descend" ? "desc" : "asc";
};

const getReceiveConditions = (coupon: Coupon) => {
  const conditions: { emoji: string; text: string; tooltip: string }[] = [];

  // Minimum receive amount
  if (coupon.minReceiveAmount && parseFloat(coupon.minReceiveAmount) > 0) {
    const amount = parseFloat(coupon.minReceiveAmount) / 100;
    conditions.push({
      emoji: "💰",
      text: `${amount.toFixed(2)}`,
      tooltip: `Min. ${amount.toFixed(2)} ${coupon.merchant.currency} Einkaufswert zum Erhalten`,
    });
  }

  // POS/Module restrictions for receiving (prevendingMachines)
  if (
    (coupon.prevendingMachines && coupon.prevendingMachines.length > 0) ||
    (coupon.prevendingMachineTypes && coupon.prevendingMachineTypes.length > 0)
  ) {
    let posText = "POS";
    let posTooltip = "";

    if (coupon.prevendingMachines && coupon.prevendingMachines.length > 0) {
      if (coupon.prevendingMachines.length === 1) {
        posText = coupon.prevendingMachines[0].vmName.substring(0, 4);
        posTooltip = `Nur an POS: ${coupon.prevendingMachines[0].vmName}`;
      } else {
        posText = `${coupon.prevendingMachines.length}x`;
        posTooltip = `Nur an bestimmten POS (${coupon.prevendingMachines.length})`;
      }
    } else if (coupon.prevendingMachineTypes && coupon.prevendingMachineTypes.length > 0) {
      const types = coupon.prevendingMachineTypes.map((type) => type.description);
      posText = types[0].substring(0, 4);
      posTooltip = `Nur an POS-Typen: ${types.join(", ")}`;
    }

    conditions.push({
      emoji: "🏦",
      text: posText,
      tooltip: posTooltip,
    });
  }

  // Payment method restrictions for receiving (prenetworks)
  if (coupon.prenetworks && coupon.prenetworks.length > 0) {
    const networks = coupon.prenetworks.map((network) => network.brand);
    conditions.push({
      emoji: "💳",
      text: networks.length > 1 ? `${networks.length}x` : networks[0].substring(0, 4),
      tooltip: `Nur mit Zahlung: ${networks.join(", ")}`,
    });
  }

  // Time restrictions (receive period)
  if (coupon.receiveStart || coupon.receiveEnd) {
    conditions.push({
      emoji: "⏰",
      text: "Zeit",
      tooltip: "Aktionszeitraum begrenzt",
    });
  }

  return conditions;
};

const getUseConditions = (coupon: Coupon) => {
  const conditions: { emoji: string; text: string; tooltip: string }[] = [];

  // Minimum use amount
  if (coupon.minUseAmount && parseFloat(coupon.minUseAmount) > 0) {
    const amount = parseFloat(coupon.minUseAmount) / 100;
    conditions.push({
      emoji: "💰",
      text: `${amount.toFixed(2)}`,
      tooltip: `Min. ${amount.toFixed(2)} ${coupon.merchant.currency} Einkaufswert zum Einlösen`,
    });
  }

  // POS/Module restrictions for usage
  if (
    (coupon.vendingMachines && coupon.vendingMachines.length > 0) ||
    (coupon.vendingMachineTypes && coupon.vendingMachineTypes.length > 0)
  ) {
    let posText = "POS";
    let posTooltip = "";

    if (coupon.vendingMachines && coupon.vendingMachines.length > 0) {
      if (coupon.vendingMachines.length === 1) {
        posText = coupon.vendingMachines[0].vmName.substring(0, 4);
        posTooltip = `Nur an POS: ${coupon.vendingMachines[0].vmName}`;
      } else {
        posText = `${coupon.vendingMachines.length}x`;
        posTooltip = `Nur an bestimmten POS (${coupon.vendingMachines.length})`;
      }
    } else if (coupon.vendingMachineTypes && coupon.vendingMachineTypes.length > 0) {
      const types = coupon.vendingMachineTypes.map((type) => type.description);
      posText = types[0].substring(0, 4);
      posTooltip = `Nur an POS-Typen: ${types.join(", ")}`;
    }

    conditions.push({
      emoji: "🏦",
      text: posText,
      tooltip: posTooltip,
    });
  }

  // Payment method restrictions for usage
  if (coupon.networks && coupon.networks.length > 0) {
    const networks = coupon.networks.map((network) => network.brand);
    conditions.push({
      emoji: "💳",
      text: networks.length > 1 ? `${networks.length}x` : networks[0].substring(0, 4),
      tooltip: `Nur mit Zahlung: ${networks.join(", ")}`,
    });
  }

  return conditions;
};

// Components
type MerchantSelectProps = {
  onChange(mid: any): void;
  value: string;
};

const MerchantSelect: React.FC<MerchantSelectProps> = ({ onChange, value }) => {
  const { data: merchants, isLoading } = useQuery("merchants", () => api.merchants.findAll());

  return (
    <Select
      showSearch
      style={{ width: 200 }}
      placeholder="Händler wählen"
      optionFilterProp="children"
      onChange={onChange}
      loading={isLoading}
      allowClear
      value={value === "" ? undefined : value}
    >
      {merchants
        ?.sort((a, b) => a.name.localeCompare(b.name))
        .map((merchant) => (
          <Option value={merchant.mid} key={merchant.mid}>
            {merchant.name}
          </Option>
        ))}
    </Select>
  );
};

const ConditionsBadge: React.FC<{
  conditions: Array<{ emoji: string; text: string; tooltip: string }>;
  type: "receive" | "use";
}> = ({ conditions, type }) => {
  if (conditions.length === 0) {
    return <span style={{ color: "#52c41a", fontSize: "12px" }}>─</span>;
  }

  const isReceiveType = type === "receive";
  const badgeStyle = {
    fontSize: "11px",
    backgroundColor: isReceiveType ? "#fff2e8" : "#f6ffed",
    padding: "2px 4px",
    borderRadius: "3px",
    border: isReceiveType ? "1px solid #ffbb96" : "1px solid #b7eb8f",
    display: "flex",
    alignItems: "center",
    gap: "2px",
    cursor: "help",
    color: isReceiveType ? "#d4380d" : "#389e0d",
  };

  return (
    <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
      {conditions.map((condition, index) => (
        <Tooltip key={index} title={condition.tooltip}>
          <span style={badgeStyle}>
            {condition.emoji}
            <span style={{ fontSize: "10px" }}>{condition.text}</span>
          </span>
        </Tooltip>
      ))}
    </div>
  );
};

const Coupons = () => {
  const intl = useIntl();

  // Pagination state
  const initialPageIndex = +(sessionStorage.getItem("pageIndex") || 0);
  const initialEntriesSize = +(sessionStorage.getItem("entriesSize") || 10);
  const [pagination, setPagination] = useState({
    index: initialPageIndex,
    entries: initialEntriesSize,
  });

  // Sorting state
  const [couponSort, setSort] = useState(localStorage.getItem("couponSort") || "cid");
  const [couponDirection, setDirection] = useState(localStorage.getItem("couponDirection") || "desc");

  // Data state
  const [data, setData] = useState<Coupon[]>([]);
  const [pageSize, setPageSize] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Filter state
  const [statusFilter, setStatusFilter] = useState<string | undefined>("all");
  const [searchText, setSearchText] = useState<string>("");
  const [merchantId, setMerchantId] = useState<string>("");

  // Load and sort data
  useEffect(() => {
    setIsLoading(true);

    const searchParam = searchText || undefined;
    const merchantParam = merchantId || undefined;

    const statusParam = statusFilter !== "all" ? statusFilter : undefined;

    Promise.all([
      api.coupons.findAll(
        pagination.entries,
        pagination.index,
        couponSort,
        couponDirection,
        merchantParam,
        searchParam,
        statusParam
      ),
      api.coupons.size(merchantParam, searchParam, statusParam),
    ]).then(([coupons, size]) => {
      // Server-side filtering is now handled, just apply sorting
      const processedData = sortCouponsData(coupons);

      setData(processedData);
      setPageSize(size);
      setIsLoading(false);
    });

    // Persist sorting preferences
    localStorage.setItem("couponSort", couponSort);
    localStorage.setItem("couponDirection", couponDirection);
  }, [pagination, couponSort, couponDirection, searchText, merchantId, statusFilter]);

  // Get display data (filtering handled server-side except status)
  const displayData = data;

  // Helper function for sorting direction
  const getDirection = (type: any): string => {
    return type === "" ? "asc" : type === "descend" ? "desc" : "asc";
  };

  const columns = [
    {
      title: "Status",
      dataIndex: "status",
      render: (_: any, coupon: Coupon) => {
        const status = getCouponStatus(coupon);
        return <Badge color={STATUS_COLORS[status]} text={STATUS_LABELS[status]} />;
      },
      sorter: (a: any, b: any, _: any) => {
        const statusA = getCouponStatus(a);
        const statusB = getCouponStatus(b);
        const statusOrder = { active: 0, pending: 1, expired: 2 };
        return statusOrder[statusA] - statusOrder[statusB];
      },
    },
    {
      title: <FormattedMessage id="coupon.table.title" />,
      dataIndex: "name",
      render: (_: String, coupon: Coupon) => <span>{coupon.name}</span>,
    },
    {
      title: <FormattedMessage id="coupon.table.amount" />,
      dataIndex: "amount",
      render: (record: String, coupon: Coupon) => (
        <>
          {coupon.amountType !== "percent" ? (
            <FormattedNumber value={Number(record) / 100} style="currency" currency={coupon.merchant.currency} />
          ) : (
            <span>{Number(record) / 100 + " %"}</span>
          )}
        </>
      ),
      sorter: (a: any, b: any, type: any) => {
        setSort("amount");
        setDirection(getDirection(type));
        return a?.amount - b?.amount;
      },
    },
    {
      title: <FormattedMessage id="coupon.table.merchant" />,
      dataIndex: "merchant",
      render: (record: Merchant) => record?.name,
      sorter: (a: any, b: any, type: any) => {
        setSort("merchant.name");
        setDirection(getDirection(type));
        return a.merchant?.name?.localeCompare(b.merchant?.name);
      },
    },
    {
      title: <FormattedMessage id="coupon.table.couponPeriod" />,
      dataIndex: "useStart",
      render: (_: String, coupon: Coupon) => (
        <>
          <span>
            {coupon.useStart ? (
              intl.formatDate(coupon.useStart, { year: "numeric", month: "numeric", day: "numeric" }) +
              "-" +
              intl.formatDate(coupon.useEnd, { year: "numeric", month: "numeric", day: "numeric" })
            ) : (
              <FormattedMessage id="coupon.card.noperiod" />
            )}
          </span>
        </>
      ),
      sorter: (a: any, b: any, type: any) => {
        setSort("useStart");
        setDirection(getDirection(type));
        return a.useStart?.localeCompare(b.useStart);
      },
    },
    {
      title: "Verwendung",
      dataIndex: "multiUse",
      render: (_: any, coupon: Coupon) => (
        <Tag color={coupon.multiUse ? "blue" : "default"}>{coupon.multiUse ? "Mehrfach" : "Einmalig"}</Tag>
      ),
      sorter: (a: any, b: any, _: any) => {
        return Number(b.multiUse) - Number(a.multiUse); // Multi-use first
      },
    },
    {
      title: <div>Erhaltsbedingungen</div>,
      dataIndex: "receiveConditions",
      render: (_: any, coupon: Coupon) => <ConditionsBadge conditions={getReceiveConditions(coupon)} type="receive" />,
    },
    {
      title: <div>Einlösebedingungen</div>,
      dataIndex: "useConditions",
      render: (_: any, coupon: Coupon) => <ConditionsBadge conditions={getUseConditions(coupon)} type="use" />,
    },
    {
      title: <FormattedMessage id="vendingmachines.table.column.actions" />,
      render: (_: string, record: Coupon) => (
        <>
          <Tooltip title="Edit">
            <Link to={"" + record.cid}>
              <Button icon={<EditOutlined />} type="text" />
            </Link>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Coupons"
        extra={[
          <MerchantSelect key={1} onChange={setMerchantId} value={merchantId} />,
          <Select
            key={2}
            placeholder="Status filtern"
            style={{ width: 150 }}
            value={statusFilter || "all"}
            onChange={setStatusFilter}
          >
            <Option value="all">Alle</Option>
            <Option value="active">Gültig</Option>
            <Option value="pending">Ausstehend</Option>
            <Option value="expired">Abgelaufen</Option>
          </Select>,
          <Link to="new" key={3}>
            <Button type="primary" icon={<PlusOutlined />}>
              <FormattedMessage id="coupons.new-coupon" />
            </Button>
          </Link>,
        ]}
      />
      <div style={{ margin: "16px 24px 0" }}>
        <Input
          placeholder={intl.formatMessage({ id: "common.search" })}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <Table
          rowKey={(record: Merchant) => record.mid}
          /*rowSelection={{
            type: "checkbox",
          }}*/
          columns={columns}
          dataSource={displayData}
          pagination={false}
          loading={isLoading}
          onChange={() => {
            setPagination({ index: 0, entries: initialEntriesSize });
          }}
        />
        <Row justify="end">
          <b style={{ margin: 10 }}>
            {pagination.index * pagination.entries}-{pagination.index * pagination.entries + displayData.length}{" "}
            {intl.formatMessage({ id: "antd.table.pagination.of" })} {pageSize}{" "}
            {intl.formatMessage({ id: "antd.table.pagination.items" })}
          </b>

          <CustomPaginationNav
            index={pagination.index}
            pageSize={pageSize}
            index_entries_datalength={pagination.index * pagination.entries + displayData.length}
            entries={Number(sessionStorage.getItem("entriesSize")) || 10}
            onPrev={() => {
              setPagination({ index: pagination.index - 1, entries: pagination.entries });
              sessionStorage.setItem("pageIndex", `${pagination.index - 1}`);
            }}
            onNext={() => {
              setPagination({ index: pagination.index + 1, entries: pagination.entries });
              sessionStorage.setItem("pageIndex", `${pagination.index + 1}`);
            }}
            onSelect={(value) => {
              sessionStorage.setItem("entriesSize", `${value}`);
              setPagination({ index: pagination.index, entries: Number(value) });
            }}
          />
        </Row>
      </div>
    </>
  );
};

export default Coupons;
